import fs from 'fs';
import path from 'path';
import type { Stats } from 'fs';

import { ASTHttpClient, type VisitorRulesV2 } from '@coderule/clients';

import type { CompiledRulesBundle } from '../types';
import { logger } from '../log/logger';

function toPosix(input: string): string {
  return input.split(path.sep).join('/');
}

function getLowerBasename(input: string): string {
  const base = input.split('/').pop();
  return (base ?? '').toLowerCase();
}

function getLowerExt(basename: string): string {
  const idx = basename.lastIndexOf('.');
  if (idx < 0) return '';
  return basename.slice(idx).toLowerCase();
}

export function compileRulesBundle(rules: VisitorRulesV2): CompiledRulesBundle {
  const compiled = ASTHttpClient.compileRulesV2(rules);
  const basePredicate = ASTHttpClient.buildIgnoredPredicate(compiled);
  const predicate = (fullPath: string, stats?: Stats) => {
    let info = stats;
    if (!info) {
      logger.debug({ path: fullPath }, 'Predicate fallback lstat');
      try {
        info = fs.lstatSync(fullPath);
      } catch (error) {
        logger.warn(
          { err: error, path: fullPath },
          'Failed to lstat path for rules predicate',
        );
        return false;
      }
    }
    return basePredicate(fullPath, info);
  };
  return {
    rules,
    compiled,
    predicate,
  };
}

export function shouldIncludeFile(
  relPath: string,
  stats: Stats,
  bundle: CompiledRulesBundle,
): boolean {
  if (stats.isDirectory()) return false;
  const posixRel = toPosix(relPath);
  if (bundle.compiled.dirRe.test(posixRel)) {
    return false;
  }
  const basename = getLowerBasename(posixRel);
  if (bundle.compiled.names.has(basename)) {
    return true;
  }
  const ext = getLowerExt(basename);
  return bundle.compiled.exts.has(ext);
}

export function shouldPruneDirectory(
  relPath: string,
  bundle: CompiledRulesBundle,
): boolean {
  const posixRel = toPosix(relPath);
  return bundle.compiled.dirRe.test(posixRel);
}

export function buildWatcherIgnored(bundle: CompiledRulesBundle) {
  return (fullPath: string, stats?: Stats) => bundle.predicate(fullPath, stats);
}
